import React, { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';

/** Counts up to `value` the first time it scrolls into view. */
const StatCounter = ({ value, suffix = '', label, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(0);
  // If the count-up never runs (throttled rAF in a background tab, an
  // IntersectionObserver that never fires, a stalled frame loop), the tile
  // would sit at "0 Projects delivered" forever. Snap to the real number
  // after a short grace period and skip the animation if that happens.
  const settled = useRef(false);

  useEffect(() => {
    const failsafe = setTimeout(() => {
      if (!settled.current) {
        settled.current = true;
        setDisplay(value);
      }
    }, 3000);
    return () => clearTimeout(failsafe);
  }, [value]);

  useEffect(() => {
    if (!inView || settled.current) return undefined;
    if (reduce) {
      settled.current = true;
      setDisplay(value);
      return undefined;
    }
    let raf;
    let start;
    const duration = 1400;
    const run = (t) => {
      if (start === undefined) start = t;
      const p = Math.min((t - start) / duration, 1);
      // ease-out cubic so it decelerates into the final number
      setDisplay(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(run);
      else settled.current = true;
    };
    const timer = setTimeout(() => {
      raf = requestAnimationFrame(run);
    }, delay * 1000);
    return () => {
      clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [inView, value, reduce, delay]);

  return (
    <div ref={ref} className="text-center">
      <div className="font-display text-4xl md:text-5xl font-bold text-white tabular-nums">
        {display}
        <span className="text-secondary">{suffix}</span>
      </div>
      <div className="mt-2 text-sm md:text-base text-white/60">{label}</div>
    </div>
  );
};

export default StatCounter;
